import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { MenuApiService } from '../menu-api.service';
import { MenuStoreService } from '../menu-store.service';
import { NotificationService } from '@emm/shared/services/notification.service';
import {AttributesActions} from '@emm/menu/store/attributes/attributes.actions';
import {MenuBaseActions} from '@emm/menu/store/base/menu-base.actions';
import {ChangeSetsActions} from '@emm/menu/store/change-sets/change-sets.actions';
import {ChangeSetsAsyncActions} from '@emm/menu/store/change-sets/change-sets.actions';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {ConsumersAsyncActions, ConsumerTypesActions} from '@emm/menu/store/consumer-types/consumer-types.actions';
import {ConsumersActions} from '@emm/menu/store/consumer/consumers.actions';
import {LocationSetsActions} from '@emm/menu/store/location-sets/location-sets.actions';
import {LocationSetTypesActions} from '@emm/menu/store/location-set-types/location-set-types.actions';
import {ItemChildrenActions} from '@emm/menu/store/item-children/item-children.actions';
import {ItemsActions} from '@emm/menu/store/items/items.actions';
import {ItemDetailAsyncActions} from '@emm/menu/store/item-detail/item-detail.actions';

@Injectable({
    providedIn: 'root'
})
export class MenuBaseEffects {

    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MenuBaseActions.init),
            switchMap(() =>
                from([
                    // TODO: decide between Base having all load actions or each load action being handled in their respective effects
                    //        -- see Management for alternative
                    LocationSetTypesActions.load(),
                    AttributesActions.loadAll(),
                    ConsumersActions.loadAll(),
                    ChangeSetsActions.loadAllFutureChangeSets(),
                ])
            )
        )
    );

    // change date on full menu scopes
    changeEffectiveDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MenuBaseActions.changeEffectiveDate),
            withLatestFrom(
                this.store.queryConsumers.menuParams$,
                this.store.queryFeatureScope.selected$),
            filter(([_, menuParams, featureScope]) => featureScope === FeatureScope.Viewer),
            filter(([_, menuParams]) => {
                return (
                    RA.isNotNil(menuParams) &&
                    RA.isNotNil(menuParams.menuKey) &&
                    RA.isNotNil(menuParams.menuKey.consumer) &&
                    RA.isNotNil(menuParams.menuKey.location)
                );
            }),
            switchMap(() => of(ItemsActions.load()))
        )
    );

    // change date on submenu scopes
    changeEffectiveDateSubmenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MenuBaseActions.changeEffectiveDate),
            withLatestFrom(
                this.store.queryConsumers.menuParamsContext$,
                this.store.queryFeatureScope.selected$
            ),
            filter(([_, menuParams, featureScope]) => featureScope === FeatureScope.Global || featureScope === FeatureScope.Client),
            filter(([_, menuParams, featureScope]) => {
                return (
                    RA.isNotNil(menuParams) &&
                    RA.isNotNil(menuParams.menuKey) &&
                    RA.isNotNil(menuParams.menuKey.location) &&
                    RA.isNotNil(menuParams.menuKey.submenu)
                );
            }),
            switchMap(() => of(ItemsActions.load()))
        )
    );

    errors$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    AttributesActions.loadAllFailure,
                    ...ChangeSetsAsyncActions.errorActions,
                    ConsumerTypesActions.loadAllFailure,
                    ...ConsumersAsyncActions.errorActions,
                    ItemChildrenActions.loadFailure,
                    ...ItemDetailAsyncActions.errorActions,
                    ItemsActions.loadFailure,
                    LocationSetTypesActions.loadFailure,
                    LocationSetsActions.loadAllByScopeFailure,
                    LocationSetsActions.loadLocationsByParentFailure
                ),
                tap((error) => {
                    if (error?.error?.showError) {
                        const defaultErrorMessage = `Server side error loading menu.`;
                        const errorMessage = error?.error?.error?.message || error?.error || defaultErrorMessage;
                        this.notificationService.error(errorMessage);
                    }
                })
            ),
        { dispatch: false }
    );

    constructor(
        private actions$: Actions,
        private api: MenuApiService,
        private store: MenuStoreService,
        private notificationService: NotificationService
    ) {}

}
